/**
 * Grouping the flat /markets array into events.
 *
 * /markets is a FLAT ARRAY OF MARKETS, each with its own selections — not
 * fixtures containing markets. The event-level view the stakeholder asked for
 * (sport → match → every market on that match) is therefore a client-side
 * grouping: every market id is `${fixtureId}:${marketKey}` (0031 durable ids,
 * e.g. `957c1fcf15961ba8:h2h`), so the fixture is the prefix before the first
 * colon. Markets that do not carry the separator (the smoke fixture
 * `smoke-market`, say) group under their own id and still render.
 *
 * Today the feed supplies one market per fixture (h2h / win); the event page
 * is built so that the moment the adapter maps spreads, totals and props, they
 * appear under the same event with no web change.
 */
import type { Market } from '@/types/api';

export interface EventGroup {
  fixtureId: string;
  sport: string;
  league: string | null | undefined;
  eventLabel: string;
  startAt: string;
  live: boolean;
  markets: Market[];
}

export function fixtureIdOf(market: Pick<Market, 'id'>): string {
  const i = market.id.indexOf(':');
  return i === -1 ? market.id : market.id.slice(0, i);
}

/** Order markets inside an event: the primary (h2h / win) first, then the rest by label. */
const PRIMARY_KEYS = new Set(['h2h', 'win', 'match_winner', 'moneyline']);

export function marketKeyOf(market: Pick<Market, 'id'>): string {
  const i = market.id.indexOf(':');
  return i === -1 ? '' : market.id.slice(i + 1);
}

export function sortMarketsForEvent(markets: Market[]): Market[] {
  return [...markets].sort((a, b) => {
    const pa = PRIMARY_KEYS.has(marketKeyOf(a)) ? 0 : 1;
    const pb = PRIMARY_KEYS.has(marketKeyOf(b)) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return (a.marketLabel ?? '').localeCompare(b.marketLabel ?? '');
  });
}

export function groupByFixture(markets: Market[] | undefined): EventGroup[] {
  const byId = new Map<string, EventGroup>();
  for (const m of markets ?? []) {
    const id = fixtureIdOf(m);
    const g = byId.get(id);
    if (g) {
      g.markets.push(m);
      g.live = g.live || m.live === true;
    } else {
      byId.set(id, {
        fixtureId: id,
        sport: m.sport,
        league: m.league,
        eventLabel: m.eventLabel,
        startAt: m.startAt,
        live: m.live === true,
        markets: [m],
      });
    }
  }
  const groups = [...byId.values()];
  for (const g of groups) g.markets = sortMarketsForEvent(g.markets);
  groups.sort((a, b) => a.startAt.localeCompare(b.startAt));
  return groups;
}

export function findEvent(markets: Market[] | undefined, fixtureId: string): EventGroup | null {
  return groupByFixture(markets).find((g) => g.fixtureId === fixtureId) ?? null;
}
